/* Book cover card — Feb-2026 indie-conv redesign.
 *
 * - Narrower cover to match the 5.5"×8.5" trim ratio (aspect-[11/17]).
 * - Smaller thumbnail so more books fit per carousel.
 * - Hover / focus opens a portal-rendered popup that puts the cover
 *   art on the LEFT and the back-cover copy on the RIGHT, so the
 *   reader can browse without clicking through.
 * - Heat-flame chip (1–5) + first two trope chips painted on the front
 *   for at-a-glance shelf browsing.
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { resolveCoverUrl, trackStorefrontEvent } from '@/lib/bookstoreApi';
import { Money } from '@/contexts/CurrencyContext';
import { BookOpen, ShieldCheck, Languages, Flame, Award, AlertTriangle, Link2 } from 'lucide-react';
import useCanonicalTags from '@/lib/useCanonicalTags';
import NewTitleRibbon, { shouldShowNewTitleRibbon } from './NewTitleRibbon';   // Feb-2026 · gold ribbon
import { languageFlagMeta, primaryLanguageCode } from '@/lib/languageFlags';

// Feb-2026 · Mature-audiences shelves — canonical trigger tags rendered as
// a highlighted "Triggers" section on hover. The set here mirrors the
// backend `content_tags` vocabulary; anything present on `book.content_tags`
// OR `book.trigger_warnings` gets promoted into the top-priority row.
const TRIGGER_TAG_LABELS = {
  kidnapping:                'Kidnapping',
  captivity:                 'Captivity',
  non_consent:               'Non-consent',
  'non-consent':             'Non-consent',
  dub_con:                   'Dubious consent',
  'dub-con':                 'Dubious consent',
  violence_against_protagonist: 'Violence against protagonist',
  'violence-against-protagonist': 'Violence against protagonist',
  graphic_violence:          'Graphic violence',
  stalking:                  'Stalking / obsession',
  torture:                   'Torture',
  self_harm:                 'Self-harm',
  suicide_ideation:          'Suicide ideation',
  child_loss:                'Child loss',
  sexual_assault:            'Sexual assault',
  drug_use:                  'Drug use',
};
function humanTrigger(tag) {
  const key = String(tag || '').toLowerCase().replace(/\s+/g, '_');
  return TRIGGER_TAG_LABELS[key] || TRIGGER_TAG_LABELS[tag] || tag;
}
function collectTriggers(book) {
  const tags = new Set();
  (book.trigger_warnings || []).forEach((t) => tags.add(t));
  (book.content_tags     || []).forEach((t) => {
    const k = String(t || '').toLowerCase().replace(/\s+/g, '_');
    if (TRIGGER_TAG_LABELS[k]) tags.add(t);
  });
  return Array.from(tags);
}

// iter-p3-h — Human-facing labels for the tiny language pill.
const LANG_LABELS = {
  en: 'EN', es: 'ES', ar: 'AR', zh: 'ZH', fr: 'FR', de: 'DE',
  pt: 'PT', ja: 'JA', ko: 'KO', ru: 'RU', hi: 'HI', it: 'IT',
  nl: 'NL', pl: 'PL', sv: 'SV', tr: 'TR', he: 'HE',
};
function langLabel(code) {
  if (!code) return null;
  const primary = String(code).split('-')[0].toLowerCase();
  return LANG_LABELS[primary] || primary.toUpperCase();
}

// Feb-2026 — "Same world" badge. `world_tag` is a plain slug
// ("thorn-accord") shared by every book set in the same fictional
// universe, possibly across different pen names — humanize it for
// display rather than storing a separate display label on each book.
function worldLabel(tag) {
  if (!tag) return null;
  return tag.split('-').filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
}

// Deterministic gradient picker so each book has a stable colour
// fingerprint even before AI art arrives.
const PALETTES = [
  'linear-gradient(135deg, hsl(28, 60%, 88%), hsl(20, 45%, 72%))',
  'linear-gradient(135deg, hsl(210, 35%, 28%), hsl(220, 50%, 16%))',
  'linear-gradient(135deg, hsl(345, 50%, 78%), hsl(355, 45%, 60%))',
  'linear-gradient(135deg, hsl(195, 30%, 26%), hsl(205, 45%, 16%))',
  'linear-gradient(135deg, hsl(40, 65%, 82%), hsl(28, 55%, 60%))',
  'linear-gradient(135deg, hsl(150, 25%, 35%), hsl(160, 30%, 22%))',
];
function paletteFor(id) {
  if (!id) return PALETTES[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length];
}

function HeatFlames({ level, heat, size = 'sm' }) {
  // Feb-2026 · Support new "extreme" tier — rendered as 5 red flames
  // with an "EXTREME" pill so it reads distinctly from scorching (5 orange).
  const isExtreme = String(heat || '').toLowerCase() === 'extreme';
  const n = isExtreme ? 5 : Math.min(5, Math.max(0, Number(level || 0)));
  if (!n && !isExtreme) return null;
  const dim = size === 'sm' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5';
  return (
    <span
      className={`pointer-events-none inline-flex items-center gap-[1px] rounded-full px-1.5 py-[1px] backdrop-blur-sm ${isExtreme ? 'bg-destructive/85 ring-1 ring-destructive/50' : 'bg-black/60'}`}
      title={isExtreme ? 'Extreme heat · 18+ mature audiences only' : `Heat ${n}/5`}
      aria-label={isExtreme ? 'Extreme heat, 18-plus mature audiences only' : `Heat level ${n} of 5`}
    >
      {Array.from({ length: n }).map((_, i) => (
        <Flame key={i} className={`${dim} ${isExtreme ? 'text-white/90' : 'text-orange-400'}`} strokeWidth={2.5} fill="currentColor" />
      ))}
      {isExtreme && (
        <span className="ml-0.5 text-[8px] font-bold uppercase tracking-wider text-white">EXTREME</span>
      )}
    </span>
  );
}

export function HoverPopup({ book, anchorRect, onLeave }) {
  const navigate = useNavigate();
  const front = resolveCoverUrl(book.cover_front_url);
  const gradient = paletteFor(book.id);
  const canonical = useCanonicalTags();
  const tropes = Array.isArray(book.tropes) ? book.tropes : [];
  const contentTags = Array.isArray(book.content_tags) ? book.content_tags : [];

  // Position the popup near the card, flipping to the other side if
  // there's not enough room on the right. Computed synchronously from
  // `anchorRect` during render (not state+effect) — the previous
  // version rendered at {left:0, top:0} for one frame then animated
  // to its real position via the `animate-in`/`transition` classes,
  // which made the popup slide directly under the cursor, trigger a
  // mouseleave on the card, and re-open/close in an endless ~330ms loop.
  const popRef = useRef(null);
  let pos = { left: 0, top: 0, placement: 'right' };
  if (anchorRect) {
    const POP_W = 520;
    const POP_H = 340;
    const margin = 12;
    const spaceRight = window.innerWidth  - anchorRect.right;
    const spaceLeft  = anchorRect.left;
    let left;
    let placement;
    if (spaceRight >= POP_W + margin) {
      left = anchorRect.right + margin;
      placement = 'right';
    } else if (spaceLeft >= POP_W + margin) {
      left = anchorRect.left - POP_W - margin;
      placement = 'left';
    } else {
      left = Math.max(8, (window.innerWidth - POP_W) / 2);
      placement = 'center';
    }
    let top = anchorRect.top + anchorRect.height / 2 - POP_H / 2;
    top = Math.max(12, Math.min(top, window.innerHeight - POP_H - 12));
    pos = { left, top, placement };
  }

  return ReactDOM.createPortal(
    <div
      ref={popRef}
      onMouseEnter={() => { /* keep-open */ }}
      onMouseLeave={onLeave}
      data-testid={`book-card-hover-popup-${book.id}`}
      className="fixed z-[70] w-[520px] max-w-[92vw] overflow-hidden rounded-lg border border-border bg-card text-foreground shadow-2xl animate-in fade-in zoom-in-95 duration-150"
      style={{ left: pos.left, top: pos.top, transitionProperty: 'opacity, transform' }}
      role="dialog"
      aria-label={`${book.title} — quick look`}
    >
      <div className="grid grid-cols-[180px_1fr]">
        <div
          className="relative aspect-[11/17] border-r border-border"
          style={{ backgroundImage: gradient }}
        >
          {front
            ? <img src={front} alt={book.title} className="h-full w-full object-cover" />
            : (
              <div className="flex h-full w-full flex-col items-center justify-center p-4 text-center">
                <BookOpen className="mb-2 h-8 w-8 opacity-70" />
                <div className="text-[13px] font-semibold leading-tight">{book.title}</div>
              </div>
            )}
          {/* Feb-2026 · gold "New Title" diagonal ribbon (top-left). */}
          {shouldShowNewTitleRibbon(book) && <NewTitleRibbon size="md" />}
          {(Number(book.heat_level || 0) > 0 || String(book.heat || '').toLowerCase() === 'extreme') && (
            <div className="pointer-events-none absolute left-1.5 top-1.5 z-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">
              <HeatFlames level={book.heat_level} heat={book.heat} />
            </div>
          )}
        </div>
        {/* Back-cover copy on the RIGHT */}
        <div className="flex min-w-0 flex-col p-4">
          {/* Feb-2026 · Adults-Only trigger list — pinned to the top of the
              popup so readers see triggers BEFORE the blurb. Only renders
              when the book carries trigger tags. */}
          {(() => {
            const triggers = collectTriggers(book);
            if (!triggers.length) return null;
            return (
              <div
                className="mb-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] leading-snug"
                data-testid={`book-card-hover-triggers-${book.id}`}
              >
                <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-destructive dark:text-destructive/90">
                  <AlertTriangle className="h-3 w-3" /> Content triggers
                </div>
                <div className="flex flex-wrap gap-1">
                  {triggers.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-destructive/50 bg-white/60 px-1.5 py-[1px] text-[10px] font-medium text-destructive dark:bg-destructive/20 dark:text-destructive/90"
                    >
                      {humanTrigger(t)}
                    </span>
                  ))}
                </div>
                <div className="mt-1 text-[10px] italic text-destructive/80 dark:text-destructive/70">
                  Click away to skip, or continue to the novel below.
                </div>
              </div>
            );
          })()}
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {book.author}
          </div>
          <div className="mt-0.5 truncate text-[15px] font-semibold tracking-tight" title={book.title}>
            {book.title}
          </div>
          {book.world_tag && (
            <button
              type="button"
              onClick={() => navigate(`/bookstore/world/${book.world_tag}`)}
              className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-primary hover:bg-primary/20"
              data-testid={`book-card-hover-world-tag-${book.id}`}
            >
              <Link2 className="h-2.5 w-2.5" strokeWidth={2.5} /> {worldLabel(book.world_tag)} world
            </button>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <div className="text-[13px] font-semibold text-primary">
              <Money usdCents={book.price_cents} overrides={book.price_overrides} />
            </div>
            {book.page_count ? (
              <span className="text-[10.5px] text-muted-foreground">· {book.page_count}p</span>
            ) : null}
          </div>
          <p className="mt-2 line-clamp-[9] text-[12px] leading-snug text-foreground/85 whitespace-pre-line">
            {book.back_copy || 'A novel by an indie author.'}
          </p>
          {(tropes.length > 0 || contentTags.length > 0) && (
            <div className="mt-auto pt-2">
              {tropes.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid={`book-card-hover-tropes-${book.id}`}>
                  {tropes.slice(0, 6).map((t) => (
                    <span key={t} className="rounded-full border border-primary/40 bg-primary/10 px-1.5 py-[1px] text-[9.5px] font-medium uppercase tracking-wider text-primary">
                      {t}
                    </span>
                  ))}
                </div>
              )}
              {contentTags.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {contentTags.slice(0, 4).map((t) => {
                    const canon = canonical.isCanonical(t);
                    return (
                      <span key={t} className="inline-flex items-center gap-0.5 rounded-full border border-oxblood/40 bg-oxblood/10 px-1.5 py-[1px] text-[9.5px] font-medium uppercase tracking-wider text-oxblood dark:text-destructive/80">
                        {canon && <ShieldCheck className="h-2.5 w-2.5 text-emerald-600" strokeWidth={2.5} />}
                        {t}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <div className="mt-3 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            Click cover for full details →
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function BookCard({ book, onClick, onLanguageClick }) {
  const navigate = useNavigate();
  const front = resolveCoverUrl(book.cover_front_url);
  const gradient = paletteFor(book.id);
  const tags = Array.isArray(book.content_tags) ? book.content_tags.filter(Boolean) : [];
  const tropes = Array.isArray(book.tropes) ? book.tropes.filter(Boolean) : [];
  const topTags = tags.slice(0, 2);
  const topTropes = tropes.slice(0, 2);
  const { isCanonical } = useCanonicalTags();
  const bundleSavingsPct = Number(book.max_bundle_savings_pct || 0);
  const showBundleRibbon = bundleSavingsPct >= 5;
  const cardLanguage = book.language_code || book.original_language || 'en';
  const flagMeta = languageFlagMeta(cardLanguage);
  const originalLanguage = primaryLanguageCode(book.original_language || 'en');
  const isTranslatedTitle = Boolean(
    flagMeta && (book.is_translation || book.translation_of_id || flagMeta.code !== originalLanguage),
  );

  // Delayed hover popup — avoids flashing when the reader is skimming.
  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);
  const timerRef = useRef(null);
  const openPopup = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (cardRef.current) setRect(cardRef.current.getBoundingClientRect());
    }, 220);
  };
  const closePopup = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setRect(null);
  };
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <>
      <button
        ref={cardRef}
        type="button"
        onClick={() => {
          trackStorefrontEvent({ event_type: 'book_select', book_id: book.id, genre_slug: book.genre_slug });
          onClick?.(book);
        }}
        onMouseEnter={openPopup}
        onMouseLeave={closePopup}
        onFocus={openPopup}
        onBlur={closePopup}
        data-testid={`book-card-${book.id}`}
        className="group relative inline-flex aspect-[11/17] w-32 shrink-0 flex-col items-stretch text-left outline-none transition-transform focus-visible:z-30 hover:z-30 hover:-translate-y-1"
      >
        <div
          className="absolute inset-0 overflow-hidden rounded-md border border-border bg-card shadow-panel-active"
          style={front ? undefined : { backgroundImage: gradient }}
        >
          {front && <img src={front} alt={book.title} className="h-full w-full object-cover" loading="lazy" />}
          {!front && (
            <div className="flex h-full w-full flex-col items-center justify-center p-3 text-center text-foreground">
              <BookOpen className="mb-1.5 h-6 w-6 opacity-70" />
              <div className="text-[11px] font-semibold leading-tight line-clamp-3">{book.title}</div>
              <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-foreground/60">{book.author}</div>
            </div>
          )}

          {/* Feb-2026 · gold "New Title" diagonal ribbon — always top-left,
           *  above heat/OHAS/bundle chips (which are absolutely-positioned
           *  with lower z). */}
          {shouldShowNewTitleRibbon(book) && <NewTitleRibbon size="sm" />}

          {/* OHAS certification chip — tiny purple/emerald pill in the
           *  top-right of every certified card. Uses the level colour so
           *  D3 (purple) reads distinctly from D4 (amber). */}
          {book.ohas_level && (
            <span
              className="pointer-events-none absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-black/60 px-1.5 py-[1px] text-[8px] font-semibold uppercase tracking-wider text-white/95 backdrop-blur-sm"
              title={`OHAS ${book.ohas_level} — human authorship verified`}
              data-testid={`book-card-ohas-${book.id}`}
            >
              <Award className="h-2 w-2 text-purple-300" strokeWidth={2.5} />
              OHAS {book.ohas_level}
            </span>
          )}

          {/* Bundle-savings ribbon */}
          {showBundleRibbon && (
            <div
              data-testid={`book-card-bundle-ribbon-${book.id}`}
              className="pointer-events-none absolute -right-8 top-2 z-10 rotate-45 bg-oxblood px-8 py-0.5 text-[8px] font-bold uppercase tracking-[0.16em] text-white shadow-md"
            >
              Save {bundleSavingsPct}%
            </div>
          )}

          {/* Heat + content-warning chips (top-left) */}
          {(Number(book.heat_level || 0) > 0 || String(book.heat || '').toLowerCase() === 'extreme' || topTags.length > 0 || book.world_tag) && (
            <div className="pointer-events-none absolute inset-x-1 top-1 flex flex-wrap gap-1"
                 data-testid={`book-card-content-tags-${book.id}`}>
              {(Number(book.heat_level || 0) > 0 || String(book.heat || '').toLowerCase() === 'extreme') && (
                <span data-testid={`book-card-heat-${book.id}`}>
                  <HeatFlames level={book.heat_level} heat={book.heat} />
                </span>
              )}
              {book.world_tag && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); navigate(`/bookstore/world/${book.world_tag}`); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); navigate(`/bookstore/world/${book.world_tag}`); } }}
                  className="pointer-events-auto inline-flex cursor-pointer items-center gap-0.5 rounded-full bg-primary/80 px-1.5 py-[1px] text-[8px] font-semibold uppercase tracking-wider text-primary-foreground backdrop-blur-sm hover:bg-primary"
                  title={`See the ${worldLabel(book.world_tag)} collection`}
                  aria-label={`See the ${worldLabel(book.world_tag)} collection`}
                  data-testid={`book-card-world-tag-${book.id}`}
                >
                  <Link2 className="h-2 w-2" strokeWidth={2.5} />
                  Same World
                </span>
              )}
              {topTags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-0.5 rounded-full bg-black/60 px-1.5 py-[1px] text-[8px] font-medium uppercase tracking-wider text-white/90 backdrop-blur-sm"
                >
                  {isCanonical(t) && <ShieldCheck className="h-2 w-2 text-emerald-300" strokeWidth={2.5} />}
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Trope chips (bottom-left, above the language pill) */}
          {topTropes.length > 0 && (
            <div
              className="pointer-events-none absolute inset-x-1 bottom-6 flex flex-wrap gap-1"
              data-testid={`book-card-tropes-${book.id}`}
            >
              {topTropes.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-primary/85 px-1.5 py-[1px] text-[8px] font-semibold uppercase tracking-wider text-primary-foreground backdrop-blur-sm"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Language pill */}
          {langLabel(cardLanguage) && (
            onLanguageClick ? (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onLanguageClick(cardLanguage); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onLanguageClick(cardLanguage); } }}
                className="pointer-events-auto absolute bottom-1 right-1 z-10 inline-flex cursor-pointer items-center gap-0.5 rounded-full bg-black/60 px-1.5 py-[1px] text-[8.5px] font-semibold uppercase tracking-wider text-white/90 backdrop-blur-sm hover:bg-black/80"
                data-testid={`book-card-language-${book.id}`}
                aria-label={`Filter this shelf to ${langLabel(cardLanguage)}`}
              >
                {flagMeta ? (
                  <img src={flagMeta.flagUrl} alt="" className="h-2.5 w-2.5 rounded-[1px] object-cover" />
                ) : (
                  <Languages className="h-2 w-2" strokeWidth={2.5} />
                )}
                {langLabel(cardLanguage)}
              </span>
            ) : (
              <span
                className="pointer-events-none absolute bottom-1 right-1 inline-flex items-center gap-0.5 rounded-full bg-black/60 px-1.5 py-[1px] text-[8.5px] font-semibold uppercase tracking-wider text-white/90 backdrop-blur-sm"
                data-testid={`book-card-language-${book.id}`}
              >
                {flagMeta ? (
                  <img src={flagMeta.flagUrl} alt="" className="h-2.5 w-2.5 rounded-[1px] object-cover" />
                ) : (
                  <Languages className="h-2 w-2" strokeWidth={2.5} />
                )}
                {langLabel(cardLanguage)}
              </span>
            )
          )}
        </div>

        {/* Title strip under the card */}
        <div className="absolute inset-x-0 -bottom-11 text-center">
          <div className="flex min-w-0 items-center justify-center gap-1" data-testid={`book-card-title-${book.id}`}>
            <div className="truncate text-[11px] font-semibold tracking-tight">{book.title}</div>
            {flagMeta && (
              <img
                src={flagMeta.flagUrl}
                alt={isTranslatedTitle ? `${flagMeta.label} translation` : `${flagMeta.label} title`}
                title={isTranslatedTitle ? `${flagMeta.label} translation` : `${flagMeta.label} title`}
                className="h-3 w-3 shrink-0 rounded-[1px] object-cover"
                data-testid={`book-card-title-language-${book.id}`}
              />
            )}
          </div>
          <div className="truncate text-[9.5px] text-muted-foreground">{book.author}</div>
          <div className="text-[9.5px] text-primary">
            <Money usdCents={book.price_cents} overrides={book.price_overrides} />
          </div>
        </div>
      </button>

      {rect && (
        <HoverPopup book={book} anchorRect={rect} onLeave={closePopup} />
      )}
    </>
  );
}
